import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "../constants/application";
import { COUNTRY_CODE } from "../constants/form";
import { EXCLUDE_COUNTRY_CODE_REGEX } from "../utils/regex";

/**
 * Formats a number according to the specified locale and options.
 *
 * This function uses the `Intl.NumberFormat` constructor to format the number,
 * allowing for localization and customization through options.
 *
 * @param {number} number - The number to be formatted.
 * @param {string} [locales='en-US'] - A string with a BCP 47 language tag, or an array of such strings.
 * @param {Object} [options={}] - An object with formatting options. Follows the same structure as the options parameter of `Intl.NumberFormat`.
 * @returns {string} - The formatted number as a string.

 */
export const formatNumber = (number, locales = "en-US", options = {}) => {
  return new Intl.NumberFormat(locales, options).format(number);
};

/**
 * Formats a Date object into 'DD/MM/YYYY' string format.
 *
 * This function takes a Date object and returns a string representing the date
 * in the 'DD/MM/YYYY' format using Day.js.
 *
 * @param {Date} date - The date object to be formatted.
 * @param {format} date - The date format.
 * @returns {string} - The formatted date string.
 *
 */
export const formatDate = (date, format) => {
  return dayjs(date).format(format);
};

/**
 * Returns a date a certain number of months before the given date.
 *
 * @param {string|Date} [date=new Date()] - The starting date. Defaults to the current date if not provided.
 * @param {number} [months=1] - The number of months to subtract. Defaults to 1 if not provided.
 * @param {string} [format='DD/MM/YYYY'] - The format for the output date. Defaults to 'DD/MM/YYYY' if not provided.
 * @returns {string} - The formatted date.
 *
 * @example
 * // Returns '22/06/2024' if today is 22/07/2024
 * getPreviousDate(null, 1, 'DD/MM/YYYY');
 *
 * @example
 * // Returns '22/05/2024' if today is 22/07/2024
 * getPreviousDate(null, 2, 'DD/MM/YYYY');
 */
export const getPreviousDate = (
  date = new Date(),
  months = 1,
  format = DATE_TIME_FORMAT.DD_MM_YYYY
) => {
  return dayjs(date).subtract(months, "month").format(format);
};

/**
 * Formats a given phone number to the format +1 XXX-XXX-XXXX.
 *
 * This function takes a phone number as input and formats it to the desired format.
 * It assumes the phone number is a 10-digit number.
 *
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number.
 *
 * @example
 * // returns '+1 727-456-7893'
 * formatPhoneNumber('7274567893');
 */
export const formatPhoneNumber = (phoneNumber) => {
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${COUNTRY_CODE} ${match[1]}-${match[2]}-${match[3]}`;
  }
  return null;
};

/**
 * Excludes the country code from a phone number.
 *
 * This function takes a phone number as input and removes the country code
 * using the specified regular expression.
 *
 * @param {string} phoneNumber - The phone number with the country code.
 * @returns {string} - The phone number without the country code.
 *
 * @example
 * const phoneNumber = "+11234567890";
 * const formattedPhoneNumber = excludeCountryCodeFromPhone(phoneNumber);
 * console.log(formattedPhoneNumber); // Output: "1234567890"
 */
export const excludeCountryCodeFromPhone = (phoneNumber) => {
  return phoneNumber.replace(EXCLUDE_COUNTRY_CODE_REGEX, "");
};
